import React, { memo, useCallback, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, AlertCircle, Info } from 'lucide-react';

export type ConfirmDialogVariant = 'danger' | 'warning' | 'neutral';

export type ConfirmDialogModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  message: string;
  variant?: ConfirmDialogVariant;
  /** Default: OK */
  confirmLabel?: string;
  /** Default: onClose */
  onConfirm?: () => void;
  /** When set, shows a secondary button before the primary (e.g. Cancel). */
  cancelLabel?: string;
  onCancel?: () => void;
};

const variantConfig = {
  danger: {
    border: 'border-red-200',
    headerBg: 'bg-red-50',
    headerBorder: 'border-red-100',
    iconWrap: 'bg-red-100',
    Icon: AlertTriangle,
    iconClass: 'text-red-600',
    titleClass: 'text-red-900',
    subtitleClass: 'text-red-600/90',
    bodyClass: 'text-red-950/85',
    primaryBtn:
      'rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
  },
  warning: {
    border: 'border-amber-200',
    headerBg: 'bg-amber-50',
    headerBorder: 'border-amber-100',
    iconWrap: 'bg-amber-100',
    Icon: AlertCircle,
    iconClass: 'text-amber-600',
    titleClass: 'text-amber-900',
    subtitleClass: 'text-amber-700/90',
    bodyClass: 'text-amber-950/85',
    primaryBtn:
      'rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2',
  },
  neutral: {
    border: 'border-gray-200',
    headerBg: 'bg-gray-50',
    headerBorder: 'border-gray-100',
    iconWrap: 'bg-gray-100',
    Icon: Info,
    iconClass: 'text-gray-600',
    titleClass: 'text-gray-900',
    subtitleClass: 'text-gray-600',
    bodyClass: 'text-gray-800',
    primaryBtn:
      'rounded-lg bg-gray-800 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
  },
} as const;

/**
 * Centered overlay dialog — same shell as the materiality leave guard (backdrop, card, header + icon).
 * Use for errors, warnings, and simple confirms instead of `alert()` / `window.confirm`.
 */
export const ConfirmDialogModal = memo(function ConfirmDialogModal({
  isOpen,
  onClose,
  title,
  subtitle,
  message,
  variant = 'danger',
  confirmLabel = 'OK',
  onConfirm,
  cancelLabel,
  onCancel,
}: ConfirmDialogModalProps) {
  const titleId = useId();
  const descId = useId();
  const cfg = variantConfig[variant];
  const { Icon } = cfg;

  const stopPanelClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleConfirm = useCallback(() => {
    (onConfirm ?? onClose)();
  }, [onConfirm, onClose]);

  const handleCancel = useCallback(() => {
    (onCancel ?? onClose)();
  }, [onCancel, onClose]);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleCancel]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      <div className="absolute inset-0" aria-hidden="true" onClick={handleCancel} />
      <div
        className={`relative z-10 w-full max-w-md rounded-xl border-2 ${cfg.border} bg-white shadow-2xl overflow-hidden`}
        onClick={stopPanelClick}
      >
        <div
          className={`flex items-start gap-3 border-b ${cfg.headerBorder} ${cfg.headerBg} px-5 py-4`}
        >
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${cfg.iconWrap}`}
          >
            <Icon className={`h-5 w-5 ${cfg.iconClass}`} aria-hidden />
          </div>
          <div className="min-w-0 pt-0.5">
            <h2 id={titleId} className={`text-lg font-semibold ${cfg.titleClass}`}>
              {title}
            </h2>
            {subtitle ? (
              <p className={`mt-0.5 text-xs font-medium uppercase tracking-wide ${cfg.subtitleClass}`}>
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>
        <div id={descId} className="px-5 py-4">
          <p className={`text-sm leading-relaxed ${cfg.bodyClass}`}>{message}</p>
        </div>
        <div className="flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-5 py-4 sm:flex-row sm:justify-end">
          {cancelLabel ? (
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
            >
              {cancelLabel}
            </button>
          ) : null}
          <button type="button" onClick={handleConfirm} className={cfg.primaryBtn}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
});
